import { useEffect, useMemo, useState } from "react";
import {
  useParams,
  useRouteContext,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";
import { useLeaData } from "@/queries.ts";
import { CLRM, CLRM_Key } from "@/models/clrm.tsx";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import CLRMAddEditPopup from "@/components/records/classroom/CLRMAddEditForm";
import {
  ClassroomSearchForm,
  ClassroomSearchFormState,
} from "@/components/records/classroom/ClassroomSearchForm";
import { CSVDownloadButton } from "@/components/shared/CSVDownloadButton";
import { FiPlus } from "react-icons/fi";
import { getCurrentProgramYear } from "@/utils/dates";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type ClassroomRow = CLRM & {
  PreschoolName?: string;
};

const classroomCsvHeaders = [
  { label: "School", key: "PreschoolName" },
  { label: "Classroom Name", key: "ClassroomName" },
  { label: "Classroom ID", key: "LocalClassroomId" },
];

export default function ClassroomTablePage() {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const leaId = useParams({ from: "/$leaid" })?.leaid;
  const { data: leaData, error, isLoading } = useLeaData(leaId);

  const navigate = useNavigate({ from: "/$leaid" });

  const searchParams = useSearch({
    from: "/$leaid/_error-wrapper/records/classroom/",
  }) as { classroomId?: string };

  const initialClassroomId = searchParams?.classroomId ?? "";

  const preschools: PreschoolSummary[] = leaContext?.currentOrgPreschools ?? [];

  const classroomRecords = useMemo<CLRM[]>(() => {
    if (!leaData) return [];
    return Object.values(leaData.entities.classrooms).flatMap((x) => {
      const seenProgramYears = new Set<string>();
      return x.records.filter((record) => {
        if (seenProgramYears.has(record.ProgramYear)) return false;
        seenProgramYears.add(record.ProgramYear);
        return true;
      });
    });
  }, [leaData]);

  const tableData: ClassroomRow[] = useMemo(() => {
    return classroomRecords.map((record) => ({
      ...record,
      PreschoolName: preschools.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      PreschoolCode: record.PreschoolCode,
      ClassroomName: record.ClassroomName,
      LocalClassroomId: record.LocalClassroomId,
      StaffAssignments: record.StaffAssignments,
      ProgramYear: record.ProgramYear,
    }));
  }, [classroomRecords, preschools]);

  const columns: GridColumnProps[] = [
    {
      id: "PreschoolName",
      field: "PreschoolName",
      title: "School",
    },
    {
      id: "ClassroomName",
      field: "ClassroomName",
      title: "Classroom Name",
    },
    {
      id: "LocalClassroomId",
      field: "LocalClassroomId",
      title: "Classroom ID",
    },
  ];

  const programYearOptions = useMemo(
    () =>
      Array.from(
        new Set(classroomRecords.map((c) => c.ProgramYear).filter(Boolean))
      ),
    [classroomRecords]
  );

  const isInitialLoadDone = !isLoading;

  const [appliedFilters, setAppliedFilters] =
    useState<ClassroomSearchFormState>(() => ({
      programYear: getCurrentProgramYear(),
      schools: [],
      classroomName: "",
      classroomId: initialClassroomId,
    }));

  useEffect(() => {
    if (!isInitialLoadDone) return;

    setAppliedFilters((prev) => ({
      ...prev,
      classroomId: initialClassroomId,
    }));
  }, [initialClassroomId, isInitialLoadDone]);

  const [popupOpen, setPopupOpen] = useState<boolean>(false);

  const handleSearch = (filters: ClassroomSearchFormState) => {
    if (!isInitialLoadDone) return;
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    navigate({
      to: "/$leaid/records/classroom",
      params: { leaid: leaId },
    });
  };

  const filteredData: ClassroomRow[] = useMemo(() => {
    if (!isInitialLoadDone) return tableData;

    const selectedSchoolCodes = appliedFilters.schools.map(
      (s) => s.PreschoolCDSCode
    );

    const year = appliedFilters.programYear.trim();
    const name = appliedFilters.classroomName.trim().toLowerCase();
    const id = appliedFilters.classroomId.trim().toLowerCase();

    return tableData.filter((row) => {
      if (year && row.ProgramYear !== year) return false;

      if (
        selectedSchoolCodes.length > 0 &&
        !selectedSchoolCodes.includes(row.PreschoolCode ?? "")
      ) {
        return false;
      }

      if (name) {
        const rowName = (row.ClassroomName ?? "").toLowerCase();
        if (!rowName.includes(name)) return false;
      }

      if (id) {
        const rowId = (row.LocalClassroomId ?? "").toLowerCase();
        if (!rowId.includes(id)) return false;
      }

      return true;
    });
  }, [tableData, appliedFilters, isInitialLoadDone]);

  const onAddButtonClick = () => {
    setPopupOpen(true);
  };

  return (
    <LayoutContentArea
      headingText="Classroom Records"
      headerActions={
        <HeaderActions
          onAddButtonClick={onAddButtonClick}
          data={filteredData}
        />
      }
    >
      <Card className="card-custom mb-5 shadow-none">
        <ClassroomSearchForm
          programYearOptions={programYearOptions}
          preschools={preschools}
          disabled={!isInitialLoadDone}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          initialClassroomId={initialClassroomId}
        />
      </Card>

      <Card className="table-style mb-3">
        <KendoTableClientState
          key={[
            appliedFilters.programYear,
            appliedFilters.classroomId,
            appliedFilters.classroomName,
            appliedFilters.schools.map((s) => s.PreschoolCDSCode).join(","),
          ].join("-")}
          data={filteredData}
          columns={columns}
          error={error}
          filterable={false}
          onRowClick={(event: GridRowClickEvent) => {
            const classroom: CLRM = event.dataItem as CLRM;
            const entityId = CLRM_Key(classroom);
            navigate({
              to: "/$leaid/records/classroom/$entityid",
              params: { leaid: leaId, entityid: entityId },
            }).catch((error) => {
              console.error("Navigation error:", error);
            });
          }}
        />
      </Card>
      <CLRMAddEditPopup show={popupOpen} onClose={() => setPopupOpen(false)} />
    </LayoutContentArea>
  );
}

function HeaderActions({
  onAddButtonClick,
  data,
}: {
  onAddButtonClick: () => void;
  data: ClassroomRow[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  return (
    <div className="d-flex align-items-center">
      <CSVDownloadButton
        data={data}
        headers={classroomCsvHeaders}
        filename="classroom-records.csv"
      />
      {leaContext.hasRole("CLRMEdit") && (
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={onAddButtonClick}
        >
          <FiPlus />
          Add Classroom
        </button>
      )}
    </div>
  );
}
